import {
	buildTimeValue,
	padTimePart,
	parseTimeValue
} from "../../../../utils/timeValues";

export const DEFAULT_PERIOD = "AM";
export const MINUTES_PER_DAY = 1440;
export const MINUTES_PER_HOUR = 60;
export const HOURS_PER_PERIOD = 12;

export const TIME_SEARCH_FIELDS = [
	{ name: "customProperties.twelveHourSearchBy", weight: 0.75 },
	{ name: "customProperties.twentyFourHourSearchBy", weight: 0.25 }
];
export const TIME_FUSE_OPTIONS = {
	caseSensitive: false,
	include: "score",
	shouldSort: true,
	threshold: 0
};

const getPeriodForHour = (hour24) => hour24 >= HOURS_PER_PERIOD ? "PM" : DEFAULT_PERIOD;
const getPeriodSuffix = (period) => period === DEFAULT_PERIOD ? "a" : "p";
const getTwelveHour = (hour24) => hour24 % HOURS_PER_PERIOD || HOURS_PER_PERIOD;
const buildCompactTime = (hour, minuteText) => `${hour}${minuteText}`;

const buildTimeChoiceParts = (hour24, minute) => {
	const minuteText = padTimePart(minute);
	const hour12 = getTwelveHour(hour24);
	const period = getPeriodForHour(hour24);

	return {
		period,
		twelveHourDisplayLabel: `${hour12}:${minuteText}`,
		twelveHourLabel: `${hour12}:${minuteText}${getPeriodSuffix(period)}`,
		twentyFourHourLabel: `${padTimePart(hour24)}:${minuteText}`
	};
};

export const buildTimeChoiceValue = (hour24, minute) => buildTimeValue({
	hour: hour24,
	minute,
	second: 0
});

export const buildTimeChoiceLabel = (hour24, minute) => {
	const parts = buildTimeChoiceParts(hour24, minute);

	return `<span class="time-choice-main-label">${parts.twelveHourLabel}</span><small class="time-choice-sub-label">${parts.twentyFourHourLabel}</small>`;
};

export const buildSelectedTimeChoiceLabel = (hour24, minute) => {
	const parts = buildTimeChoiceParts(hour24, minute);

	return `<span class="time-choice-main-label">${parts.twelveHourDisplayLabel}</span><small class="time-choice-sub-label">${parts.twentyFourHourLabel}</small>`;
};

export const buildTimeChoiceTextLabel = (hour24, minute) => {
	const parts = buildTimeChoiceParts(hour24, minute);

	return `${parts.twelveHourLabel} (${parts.twentyFourHourLabel})`;
};

export const buildSelectedTimeChoiceTextLabel = (hour24, minute) => {
	const parts = buildTimeChoiceParts(hour24, minute);

	return `${parts.twelveHourDisplayLabel} (${parts.twentyFourHourLabel})`;
};

const buildSearchAliases = (hour24, minute) => {
	const minuteText = padTimePart(minute);
	const hour12 = getTwelveHour(hour24);
	const period = getPeriodForHour(hour24);
	const periodText = period.toLowerCase();
	const periodSuffix = getPeriodSuffix(period);
	const twelveHourTime = `${hour12}:${minuteText}`;
	const compactTwelveHourTime = buildCompactTime(hour12, minuteText);
	const twentyFourHourTime = `${padTimePart(hour24)}:${minuteText}`;
	const compactTwentyFourHourTime = buildCompactTime(padTimePart(hour24), minuteText);
	const twelveHourSearchBy = [
		twelveHourTime,
		compactTwelveHourTime,
		`${twelveHourTime}${periodSuffix}`,
		`${twelveHourTime}${periodText}`,
		`${twelveHourTime} ${periodSuffix}`,
		`${twelveHourTime} ${periodText}`,
		`${twelveHourTime} ${period}`,
		`${compactTwelveHourTime}${periodSuffix}`,
		`${compactTwelveHourTime}${periodText}`,
		`${compactTwelveHourTime} ${periodText}`
	];
	const twentyFourHourSearchBy = [twentyFourHourTime, compactTwentyFourHourTime];

	if(hour24 === 0) {
		twentyFourHourSearchBy.push(`0:${minuteText}`);
	}

	return {
		twelveHourSearchBy,
		twentyFourHourSearchBy
	};
};

export const buildTimeChoice = (totalMinute) => {
	const hour24 = Math.floor(totalMinute / MINUTES_PER_HOUR);
	const minute = totalMinute % MINUTES_PER_HOUR;
	const searchAliases = buildSearchAliases(hour24, minute);

	return {
		value: buildTimeChoiceValue(hour24, minute),
		label: buildTimeChoiceLabel(hour24, minute),
		customProperties: {
			displayText: buildTimeChoiceTextLabel(hour24, minute),
			selectedDisplay: buildSelectedTimeChoiceLabel(hour24, minute),
			selectedDisplayText: buildSelectedTimeChoiceTextLabel(hour24, minute),
			twelveHourSearchBy: searchAliases.twelveHourSearchBy,
			twentyFourHourSearchBy: searchAliases.twentyFourHourSearchBy
		}
	};
};

export const buildTimeChoices = () => [...Array(MINUTES_PER_DAY).keys()].map(buildTimeChoice);

export const TIME_CHOICES = buildTimeChoices();
export const TIME_CHOICE_BY_VALUE = TIME_CHOICES.reduce((choicesByValue, choice) => {
	choicesByValue[choice.value] = choice;
	return choicesByValue;
}, {});

export const normalizeIncrementingStep = (value) => {
	const step = Number(value);
	return Number.isInteger(step) && step > 0 ? step : 1;
};

export const buildTimePickerChoices = (incrementingStepValue) => {
	const incrementingStep = normalizeIncrementingStep(incrementingStepValue);
	const availableChoices = [];
	const searchableChoices = [];

	TIME_CHOICES.forEach((choice, totalMinute) => {
		const minute = totalMinute % MINUTES_PER_HOUR;
		const destination = minute % incrementingStep === 0
			? availableChoices
			: searchableChoices;

		destination.push(choice);
	});

	return {
		availableChoices,
		choices: [...availableChoices, ...searchableChoices],
		incrementingStep
	};
};

export const getClosestTimeChoiceValue = (value, availableChoices) => {
	const timeParts = parseTimeValue(value);
	if(!timeParts || !availableChoices?.length) {
		return undefined;
	}

	const targetMinute = timeParts.hour * MINUTES_PER_HOUR + timeParts.minute;
	let closestChoice;
	let closestDistance = MINUTES_PER_DAY;

	availableChoices.forEach(choice => {
		const choiceParts = parseTimeValue(choice.value);
		const choiceMinute = choiceParts.hour * MINUTES_PER_HOUR + choiceParts.minute;
		const directDistance = Math.abs(choiceMinute - targetMinute);
		const distance = Math.min(directDistance, MINUTES_PER_DAY - directDistance);

		if(distance < closestDistance) {
			closestChoice = choice;
			closestDistance = distance;
		}
	});

	return closestChoice?.value;
};

export const buildTimeSelection = (value, fallbackPeriod = DEFAULT_PERIOD) => {
	const timeParts = parseTimeValue(value);

	if(!timeParts) {
		return {
			period: fallbackPeriod,
			timeValue: undefined
		};
	}

	return {
		period: getPeriodForHour(timeParts.hour),
		timeValue: buildTimeChoiceValue(timeParts.hour, timeParts.minute)
	};
};

export const getTimeChoiceDisplay = (value) => {
	const selection = buildTimeSelection(value);
	return TIME_CHOICE_BY_VALUE[selection.timeValue]?.customProperties.selectedDisplay;
};

export const getTimeChoiceTextDisplay = (value) => {
	const selection = buildTimeSelection(value);
	return TIME_CHOICE_BY_VALUE[selection.timeValue]?.customProperties.selectedDisplayText;
};

export const toggleTimePeriodValue = (value) => {
	const timeParts = parseTimeValue(value);

	if(!timeParts) {
		return undefined;
	}

	const toggledHour = (timeParts.hour + HOURS_PER_PERIOD) % (HOURS_PER_PERIOD * 2);
	return buildTimeChoiceValue(toggledHour, timeParts.minute);
};
